import _ from "lodash";
import React from "react";
import classNames from "classnames";
import helperClasses from "./NestedAccordionHelpers.scss";

import { Tooltip, Tag } from "@toddle-edu/ds-web";
import {
  MinusCircleOutlined,
  EyeHiddenOutlined,
  LockOutlined,
  LockOpenOutlined,
  LockFilled,
} from "@toddle-edu/ds-icons";

import { getSubjectLevelLabel } from "ClassRoom/modules/utils";
import {
  getIsEnabledExcusedStatusInAssignment,
  getIsAssessmentTypeConfigurationEnabled,
} from "Utils/ACLStoreUtils";

import { getMemoizedItemsFunction } from "Utils/GetMemoizedItemsFunction";
import { getMediaCardFileType } from "Utils/AttachmentHelpers";
import { getDateTimeStringsBasedOnFormat } from "Utils/DateTimeHelpers";
import { getInitials } from "Utils/GetInitials";
import { colors } from "Constants";

import {
  TASK_TYPE_WORKSHEET,
  TASK_TYPE_AI_TUTOR,
} from "Constants/stringConstants";

export const PLACEHOLDER_ITEM = "PLACEHOLDER_ITEM";

export const getItemWithDepth = ({ id, depth, items }) => {
  const item = _.find(items, { id });
  if (!item) return null;

  if (item.depth === depth) {
    return item;
  }

  return getItemWithDepth({ id: item.parent?.id, depth, items });
};

// Funtion to get all the ancestors of an item/folder
export const getAncestors = ({ items, id }) => {
  const item = _.find(items, { id });
  if (!item || !item?.parent) return [];

  const parentId = item.parent.id;
  const parent = _.find(items, { id: parentId });
  if (!parent) return [];

  return [...getAncestors({ items, id: parentId }), parent];
};

// Funtion to get all the ancestors of an item/folder
// lookup is O(1) for itemsConfig
export const getAncestorsV2 = ({ itemsConfig, id }) => {
  const item = _.get(itemsConfig, id, null);

  if (!item || !item?.parent) return [];

  const parentId = item.parent.id;
  const parent = _.get(itemsConfig, parentId, null);
  if (!parent) return [];

  return [...getAncestorsV2({ itemsConfig, id: parentId }), parent];
};

// function to get all children of a folder (including further nested children) in a flat heirarchy
export const getNestedChildren = ({ items, id }) => {
  const item = _.find(items, { id });
  if (!item) return [];
  const children = _.filter(items, ({ parent }) => parent?.id === id);

  return _.reduce(
    children,
    (result, child) => {
      return [...result, child, ...getNestedChildren({ items, id: child.id })];
    },
    []
  );
};

// function to get all children of a folder (including further nested children) in a flat heirarchy
// lookup is O(1) for itemsConfig
export const getNestedChildrenV2 = ({ itemsConfig, id }) => {
  const item = _.get(itemsConfig, id, null);

  if (!item) return [];
  const children = _.filter(itemsConfig, ({ parent }) => parent?.id === id);

  return _.reduce(
    children,
    (result, child) => {
      return [
        ...result,
        child,
        ...getNestedChildrenV2({ itemsConfig, id: child.id }),
      ];
    },
    []
  );
};

export const isUnitPlan = item => item?.resourceType === "UNIT_PLAN";

export const isFolder = item => item?.resourceType === "FOLDER";

export const isModule = item => isUnitPlan(item) || isFolder(item);

export const isAssessment = item => item?.resourceType === "ASSESSMENT";

const styles = {
  overlayInnerStyle: {
    maxWidth: "120px",
    textAlign: "center",
  },
};

const renderLockingIcon = ({ t, classes, summaryCounts }) => {
  const totalCount = _.get(summaryCounts, "assigned", 0);

  const openSubmissionsCount = _.get(summaryCounts, "unlockedSubmissions", 0);

  const tooltip =
    openSubmissionsCount > 0
      ? openSubmissionsCount === totalCount
        ? t("classRoom:task_is_open_for_submissions_for_all_students")
        : t("classRoom:task_is_open_for_submissions_for_count_students", {
            openSubmissionsCount,
            totalCount,
          })
      : t("classRoom:task_is_closed_for_submissions_for_all_students");

  const SubmissionLockingIcon =
    openSubmissionsCount > 0 ? LockOpenOutlined : LockOutlined;
  const submissionLockingLabel =
    openSubmissionsCount > 0 ? t("common:open") : t("common:closed");

  const dotContainerClass = classNames(
    helperClasses.dotContainer,
    classes.dotContainer
  );

  return (
    <>
      <Tooltip tooltip={tooltip}>
        <div className={classes.flexWithGap}>
          <SubmissionLockingIcon variant={"subtle"} size={"xxx-small"} />
          <div>{submissionLockingLabel}</div>
        </div>
      </Tooltip>
      <span className={dotContainerClass} />
    </>
  );
};

export const formatDueDate = ({ deadline, t }) => {
  return deadline
    ? `${t("common:due_on")}: ${
        getDateTimeStringsBasedOnFormat({
          inputDateTimeString: deadline,
          dateTimeFormat: "Do MMM YYYY, hh:mm a",
        }).dateTimeString
      }`
    : t("classRoom:no_due_date");
};

export const getAssignmentSubtitle = ({
  assignment,
  assessmentTypes,
  genericTags,
  displaySettings,
  t,
  classes = {},
  isInlineCreateOrEditItem,
}) => {
  /*
   ** 1. If assignment is closed, closed icon
   ** 2. Type of assessment
   ** 3. SL / HL tags
   ** 4. Category label, if present
   ** 5. If assignment is exluced, excluded icon
   ** 6. If assignment is private, private icon
   ** 7. Due / scheduled date
   */

  const showCategory = _.get(displaySettings, "assessment_category", true);
  const showDueOrScheduledDate =
    _.get(displaySettings, "due_or_scheduled_date", false) &&
    !isInlineCreateOrEditItem;

  const showLocked = _.get(displaySettings, "locked_status", true);

  const displayItems = [];
  const assessment = _.get(assignment, "content", {});
  const { isTimedTestEnabled, isStudentSubmissionEnabled } = assignment;

  const publishedAt = assignment.state?.publishedAt;
  const state = assignment.state?.state;
  const isPrivate = _.get(assignment, "isPrivate", false);
  const isExcludedFromFinalGradeCalculation = _.get(
    assignment,
    "isExcludedFromFinalGradeCalculation",
    false
  );

  const itemTypeSubtext = getItemTypeSubtext({
    t,
    resourceType: "ASSIGNMENT",
    data: assessment,
    assessmentTypes,
  });

  displayItems.push(
    <span className={classNames(classes.subtitleItem, classes.noShrink)}>
      {_.toUpper(itemTypeSubtext)}
    </span>
  );

  const academicTermLabel = _.get(assessment, "academicTerm.label");

  if (academicTermLabel) {
    displayItems.push(academicTermLabel);
  }

  const subjectLevelLabel = getSubjectLevelLabel({
    genericTags,
    fields: _.get(assessment, "fields", []),
  });
  if (subjectLevelLabel)
    displayItems.push(
      <span className={classNames(classes.subtitleItem, classes.noShrink)}>
        {subjectLevelLabel}
      </span>
    );

  const categoryLabel = _.get(assessment, "category.label");
  if (categoryLabel && showCategory)
    displayItems.push(
      <span className={classes.subtitleItem}>
        <Tooltip
          placement="bottom"
          tooltip={categoryLabel}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <span className={classes.subTitleText}>{categoryLabel}</span>
        </Tooltip>
      </span>
    );

  if (isExcludedFromFinalGradeCalculation) {
    displayItems.push(
      <div className={classNames(classes.flexWithGap, classes.subtitleItem)}>
        <Tooltip
          placement="bottom"
          tooltip={
            <span>{t("classRoom:excluded_from_final_grade_calculation")}</span>
          }
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <span className={classes.flexWithGap}>
            <MinusCircleOutlined
              size="xxx-small"
              variant="subtle"
              className={classes.noShrink}
            />
            <span className={classes.subTitleText}>{t("common:excluded")}</span>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (isPrivate) {
    displayItems.push(
      <div className={classNames(classes.flexWithGap, classes.subtitleItem)}>
        <Tooltip
          placement="bottom"
          tooltip={<span>{t("common:private")}</span>}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <span className={classes.flexWithGap}>
            <EyeHiddenOutlined size="xxx-small" variant="subtle" />
            <span className={classes.subTitleText}>{t("common:private")}</span>
          </span>
        </Tooltip>
      </div>
    );
  }

  let dueDate;
  if (state === "SCHEDULED") {
    dueDate = `${t("common:scheduled_for")}: ${
      publishedAt
        ? getDateTimeStringsBasedOnFormat({
            inputDateTimeString: publishedAt,
          }).dateTimeString
        : "-"
    }`;
  } else {
    dueDate = formatDueDate({ deadline: assignment.deadline, t });
  }
  if (dueDate && showDueOrScheduledDate)
    displayItems.push(
      <span className={classes.subtitleItem}>
        <Tooltip
          placement="bottom"
          tooltip={dueDate}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <span className={classes.subTitleText}>{dueDate}</span>
        </Tooltip>
      </span>
    );

  const renderLockIcon =
    !isPrivate &&
    !isTimedTestEnabled &&
    isStudentSubmissionEnabled &&
    state !== "DRAFT" &&
    showLocked;

  const dotContainerClass = classNames(
    helperClasses.dotContainer,
    classes.dotContainer
  );
  return (
    <div className={classes.assignmentSubtitle}>
      {renderLockIcon
        ? renderLockingIcon({
            summaryCounts: _.get(assignment, "summaryCounts", {}),
            t,
            classes,
          })
        : null}
      {displayItems.map((item, index) => (
        <>
          {index != 0 ? <span className={dotContainerClass} /> : null}
          {item}
        </>
      ))}
    </div>
  );
};

const renderBlueprintLockIcon = ({
  isLocked,
  isSyncedFromBluePrintCourse,
  classes,
  t,
}) => {
  if (!isSyncedFromBluePrintCourse) return null;

  const dotContainerClass = classNames(
    helperClasses.dotContainer,
    classes.dotContainer
  );

  const bluePrintLockIconClass = classNames(
    helperClasses.bluePrintLockIcon,
    classes.bluePrintLockIcon
  );

  return (
    <>
      <div className={bluePrintLockIconClass}>
        {isLocked ? (
          <LockFilled size="xxx-small" variant="subtle" />
        ) : (
          <LockOpenOutlined size="xxx-small" variant="subtle" />
        )}
        <span>{t("course:blueprint")}</span>
      </div>
      <span className={dotContainerClass} />
    </>
  );
};

export const getIsItemLocked = ({ item }) => {
  const isItemLocked = _.get(item, "lockingInfo.isLocked", false);
  const isInnerItemLocked = _.get(item, "item.lockingInfo.isLocked", false);
  return isItemLocked || isInnerItemLocked;
};

export const getItemTypeSubtext = ({
  t,
  resourceType,
  data = {},
  assessmentTypes,
}) => {
  switch (resourceType) {
    case "UNIT_PLAN": {
      return t("common:unit");
    }
    case "FOLDER": {
      return t("common:folder");
    }

    case "ORGANIZATION_RESOURCE":
    case "FILE": {
      if (_.isEmpty(data)) return null;

      const { type, mimeType } = data;
      return getMediaCardFileType({ type, mimeType });
    }

    case "ASSIGNMENT":
    case "ASSESSMENT": {
      const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

      const assessmentType = _.get(data, "assessmentType.value");
      let assessmentLabel = _.get(data, "taskType.label", "");
      const taskTypeGroup = _.get(data, "taskType.type");

      if (isAssessmentTypeConfigurationEnabled && _.isEmpty(assessmentLabel)) {
        assessmentLabel = _.get(assessmentTypes, [assessmentType, "label"], "");
      }

      let assessmentTypeLabelInitials = getInitials({ value: assessmentLabel });
      if (
        taskTypeGroup === TASK_TYPE_AI_TUTOR ||
        assessmentType === "ai_tutor"
      ) {
        assessmentTypeLabelInitials = "AI";
      }
      if (taskTypeGroup === TASK_TYPE_WORKSHEET) {
        assessmentTypeLabelInitials = "WS";
      }
      let assessmentAbbrevation = isAssessmentTypeConfigurationEnabled
        ? assessmentTypeLabelInitials
        : _.get(assessmentTypes, [assessmentType, "abbrevation"]);

      if (_.isEmpty(assessmentAbbrevation))
        assessmentAbbrevation = assessmentType;

      return _.toUpper(assessmentAbbrevation);
    }
  }
};

export const getSubtitle = getMemoizedItemsFunction({
  fn: ({
    assessmentTypes,
    genericTags,
    orgResource,
    t,
    isUserStudentOrParent,
    displaySettings = {},
    classes = {},
    isImportFromAnotherClass = false,
    isInlineCreateOrEditItem = false,
    isCompact,
  }) => {
    const {
      resourceType,
      item,
      childrenHierarchyInfo,
      attachment,
    } = orgResource;

    const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

    let isSyncedFromBluePrintCourse = _.get(
      orgResource,
      "isSyncedFromBluePrintCourse",
      false
    );

    const showBlueprintLockIcon =
      !isUserStudentOrParent &&
      _.get(displaySettings, "show_blueprint_lock", false);

    const subtitleContainerClass = classNames(
      helperClasses.subtitleContainer,
      classes.subtitleContainer
    );

    const dotContainerClass = classNames(
      helperClasses.dotContainer,
      classes.dotContainer
    );

    switch (resourceType) {
      case "UNIT_PLAN": {
        if (isCompact) return null;
        isSyncedFromBluePrintCourse = _.get(
          item,
          "isSyncedFromBluePrintCourse",
          false
        );

        const isLocked = getIsItemLocked({
          item,
        });

        const itemTypeSubtext = getItemTypeSubtext({ t, resourceType });
        const subtitleItems = [itemTypeSubtext];

        const itemsCount = _.get(childrenHierarchyInfo, "totalCount", 0);

        subtitleItems.push(
          _.toLower(t("common:item_count", { count: itemsCount }))
        );

        return (
          <div className={subtitleContainerClass}>
            {showBlueprintLockIcon
              ? renderBlueprintLockIcon({
                  isSyncedFromBluePrintCourse,
                  isLocked,
                  classes,
                  t,
                })
              : null}

            {subtitleItems.map((item, index) => (
              <>
                {index != 0 ? <span className={dotContainerClass} /> : null}
                {item}
              </>
            ))}
          </div>
        );
      }

      case "FOLDER": {
        if (isCompact) return null;

        const itemTypeSubtext = getItemTypeSubtext({ t, resourceType });
        const subtitleItems = [itemTypeSubtext];
        const itemsCount = _.get(childrenHierarchyInfo, "totalCount", 0);

        subtitleItems.push(
          _.toLower(t("common:item_count", { count: itemsCount }))
        );

        const isLocked = getIsItemLocked({
          item: orgResource,
        });

        return (
          <div className={subtitleContainerClass}>
            {showBlueprintLockIcon
              ? renderBlueprintLockIcon({
                  isSyncedFromBluePrintCourse,
                  isLocked,
                  classes,
                  t,
                })
              : null}

            {subtitleItems.map((item, index) => (
              <>
                {index != 0 ? <span className={dotContainerClass} /> : null}
                {item}
              </>
            ))}
          </div>
        );
      }

      case "ASSESSMENT": {
        isSyncedFromBluePrintCourse = _.get(
          item,
          "isSyncedFromBluePrintCourse",
          false
        );
        if (_.has(orgResource, "subtext")) {
          return orgResource.subtext;
        }

        if (isCompact) {
          return null;
        }

        const subtitleItems = [];

        const itemTypeSubtext = getItemTypeSubtext({
          t,
          resourceType,
          data: item,
          assessmentTypes,
        });
        subtitleItems.push(itemTypeSubtext);

        if (isImportFromAnotherClass) {
          return subtitleItems;
        } else {
          const academicTermLabel = _.get(item, "academicTerm.label");

          const showTermLabel = _.get(displaySettings, "grading_period", true);

          if (academicTermLabel && showTermLabel) {
            subtitleItems.push(academicTermLabel);
          }

          const showCategory = _.get(
            displaySettings,
            "assessment_category",
            true
          );

          const categoryLabel = _.get(item, "globalCategories[0].name", null);

          if (categoryLabel && showCategory) {
            subtitleItems.push(categoryLabel);
          }

          isSyncedFromBluePrintCourse = _.get(
            item,
            "isSyncedFromBluePrintCourse",
            false
          );

          const isLocked = getIsItemLocked({
            item,
          });

          return (
            <div className={subtitleContainerClass}>
              {showBlueprintLockIcon
                ? renderBlueprintLockIcon({
                    isSyncedFromBluePrintCourse,
                    isLocked,
                    classes,
                    t,
                  })
                : null}

              {subtitleItems.map((item, index) => (
                <>
                  {index != 0 ? <span className={dotContainerClass} /> : null}
                  {item}
                </>
              ))}
            </div>
          );
        }
      }

      case "ASSIGNMENT": {
        if (isImportFromAnotherClass) {
          const subtitleItems = [];

          const assessmentData = _.get(item, "content", {});
          const itemTypeSubtext = getItemTypeSubtext({
            t,
            resourceType,
            data: assessmentData,
            assessmentTypes,
          });
          subtitleItems.push(itemTypeSubtext);

          return subtitleItems;
        } else {
          return getAssignmentSubtitle({
            assignment: item,
            assessmentTypes,
            genericTags,
            displaySettings,
            t,
            classes,
            isInlineCreateOrEditItem,
            isAssessmentTypeConfigurationEnabled,
          });
        }
      }

      case "ORGANIZATION_RESOURCE":
      case "FILE": {
        const attachmentData =
          resourceType == "FILE" ? attachment : _.get(item, "attachment", {});

        const itemTypeSubtext = getItemTypeSubtext({
          t,
          resourceType,
          data: attachmentData,
        });
        const subtitleItems = [itemTypeSubtext];
        const isLocked = getIsItemLocked({
          item: orgResource,
        });

        return (
          <div className={subtitleContainerClass}>
            {showBlueprintLockIcon
              ? renderBlueprintLockIcon({
                  isSyncedFromBluePrintCourse,
                  isLocked,
                  classes,
                  t,
                })
              : null}

            {subtitleItems.map((item, index) => (
              <>
                {index != 0 ? <span className={dotContainerClass} /> : null}
                {item}
              </>
            ))}
          </div>
        );
      }
    }
  },
  resolver: params =>
    JSON.stringify(
      _.omit(params, [
        "classes",
        "assessmentTypes",
        "genericTags",
        "isUserStudentOrParent",
      ])
    ),
  items: ["LANGUAGE", "TIMEZONE"],
});

export const getStatusDetails = ({ status, submissionStatus, isEvaluated }) => {
  let styles = {
    color: colors.textDefault,
    backgroundColor: colors.surfaceDisabled,
  };
  let showStatus = true;
  const assignmentStatus = isEvaluated ? status : submissionStatus;

  switch (assignmentStatus) {
    case "TURNED_IN":
    case "TURNED_IN_ON_TIME":
    case "EVALUATED":
    case "COMPLETED": {
      styles = {
        color: colors.decorativeSurfaceFiveD,
        backgroundColor: colors.decorativeSurfaceFiveL,
      };
      break;
    }
    case "TURNED_IN_LATE": {
      styles = {
        color: colors.decorativeSurfaceOneD,
        backgroundColor: colors.decorativeSurfaceOneL,
      };
      break;
    }
    case "OVERDUE":
    case "REQUEST_RESUBMISSION_OVERDUE": {
      styles = {
        color: colors.decorativeSurfaceThreeD,
        backgroundColor: colors.decorativeSurfaceThreeL,
      };
      break;
    }
    case "NOT_SUBMITTED":
    case "RESUBMISSION_NOT_SUBMITTED": {
      styles = {
        color: colors.red42,
        backgroundColor: "#FFF6F3",
      };
      break;
    }
  }

  if (
    getIsEnabledExcusedStatusInAssignment() &&
    status === "EVALUATION_NOT_APPLICABLE" &&
    (isEvaluated ||
      !_.includes(
        ["TURNED_IN", "TURNED_IN_ON_TIME", "TURNED_IN_LATE"],
        submissionStatus
      ))
  ) {
    showStatus = false;
  }

  return {
    labelLocale: status ? `classRoom:${_.toLower(assignmentStatus)}` : "",
    styles,
    showStatus,
  };
};

export const getIsResourceShared = ({ orgResource }) => {
  if (_.isEmpty(orgResource)) return null;
  const { item, resourceType } = orgResource;
  switch (resourceType) {
    case "ASSIGNMENT": {
      const state = _.get(item, "state.state", "DRAFT");
      return state !== "DRAFT";
    }

    default:
      return orgResource.state === "PUBLISHED";
  }
};

export const getIsResourceSharedRecursively = ({ orgResource, items }) => {
  const isShared = getIsResourceShared({ orgResource });

  if (isShared) {
    const parent = _.find(items, { id: _.get(orgResource, "parent.id") });
    if (parent)
      return getIsResourceSharedRecursively({ orgResource: parent, items });
    else return true;
  } else return false;
};

export const getTag = ({ orgResource, isUserStudentOrParent, t, userType }) => {
  const { resourceType } = orgResource;

  switch (resourceType) {
    case "ASSIGNMENT": {
      const studentAssignment =
        _.head(_.get(orgResource, "item.assignedStudents", [])) || [];
      const { isEvaluationApplicable = true } = studentAssignment;

      if (
        isUserStudentOrParent &&
        getIsEnabledExcusedStatusInAssignment() &&
        !isEvaluationApplicable
      ) {
        return (
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={
              userType === "student"
                ? t("classRoom:assignment_excused_tooltip")
                : t("classRoom:assignment_excused_parent_tooltip")
            }
            placement="top"
          >
            <Tag color={"yellow"} dsVersion={"2.0"} size={"small"}>
              {t("classRoom:excused")}
            </Tag>
          </Tooltip>
        );
      }

      return null;
    }

    case "ASSESSMENT": {
      const assignment =
        _.first(_.get(orgResource, "item.assignments.edges", [])) || [];
      const studentAssignment =
        _.first(_.get(assignment, "assignedStudents", [])) || [];

      if (_.isEmpty(studentAssignment)) {
        return null;
      }

      const { isEvaluationApplicable = true } = studentAssignment;

      if (
        isUserStudentOrParent &&
        getIsEnabledExcusedStatusInAssignment() &&
        !isEvaluationApplicable
      ) {
        return (
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={
              userType === "student"
                ? t("classRoom:assignment_excused_tooltip")
                : t("classRoom:assignment_excused_parent_tooltip")
            }
            placement="top"
          >
            <Tag color={"yellow"} dsVersion={"2.0"} size={"small"}>
              {t("classRoom:excused")}
            </Tag>
          </Tooltip>
        );
      }

      return null;
    }

    default:
      return null;
  }
};
